import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Stack, Alert, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper
} from '@mui/material';
import { Add, Edit, School, MenuBook, People } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

// Use axios with auth headers from localStorage
const api = {
  get:    (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  post:   (url, data, cfg={}) => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  put:    (url, data, cfg={}) => axios.put(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  delete: (url, cfg={}) => axios.delete(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
};

const TYPES = ['certificate','diploma','degree','postgrad','other'];

const empty = { code:'', name:'', type:'degree', duration_years:3, total_credits:360, faculty:'', department:'' };

export default function Programmes() {
  const { enqueueSnackbar } = useSnackbar();
  const [programmes, setprogrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/tertiary/programmes')
      .then(r => setprogrammes(r.data.data || []))
      .catch(() => enqueueSnackbar('Failed to load programmes', { variant:'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openNew  = () => { setForm(empty); setEditing(null); setDialog(true); };
  const openEdit = (p)  => { setForm({ ...p }); setEditing(p.id); setDialog(true); };
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.code || !form.name) return enqueueSnackbar('Code and name are required', { variant:'warning' });
    setSaving(true);
    try {
      if (editing) await api.put(`/tertiary/programmes/${editing}`, form);
      else         await api.post('/tertiary/programmes', form);
      enqueueSnackbar(`Programme ${editing ? 'updated' : 'created'}`, { variant:'success' });
      setDialog(false); load();
    } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Save failed', { variant:'error' }); }
    finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this programme?')) return;
    try { await api.delete(`/tertiary/programmes/${id}`); load(); }
    catch { enqueueSnackbar('Failed', { variant:'error' }); }
  };

  const typeColor = { certificate:'info', diploma:'warning', degree:'primary', postgrad:'secondary', other:'default' };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Programmes</Typography>
          <Typography variant="body2" color="text.secondary">Manage academic programmes offered by your institution</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={{ bgcolor:'#0f2b4a' }}>
          Add Programme
        </Button>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label:'Total Programmes', value: programmes.length, icon:<School />, color:'#0f2b4a' },
          { label:'Active Students', value: programmes.reduce((s,p) => s + (p.enrolled_students||0), 0), icon:<People />, color:'#2e7d32' },
          { label:'Total Courses', value: programmes.reduce((s,p) => s + (p.total_courses||0), 0), icon:<MenuBook />, color:'#c9952a' },
        ].map(stat => (
          <Grid item xs={12} sm={4} key={stat.label}>
            <Card variant="outlined" sx={{ borderLeft:`4px solid ${stat.color}`, borderRadius:2 }}>
              <CardContent sx={{ pb:'12px !important' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize={10} textTransform="uppercase">{stat.label}</Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: stat.color }}>{stat.value}</Typography>
                  </Box>
                  <Box sx={{ color: stat.color, opacity: 0.3 }}>{stat.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Programmes table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor:'#f8f9fc' }}>
              {['Code','Programme Name','Type','Duration','Credits','Students','Courses','Actions'].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary' }}>Loading...</TableCell></TableRow>
            ) : programmes.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary' }}>No programmes yet — add your first one</TableCell></TableRow>
            ) : programmes.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><Typography fontFamily="monospace" fontWeight={700} fontSize={13}>{p.code}</Typography></TableCell>
                <TableCell>
                  <Typography fontWeight={600} fontSize={14}>{p.name}</Typography>
                  {p.faculty && <Typography variant="caption" color="text.secondary">{p.faculty}{p.department ? ` · ${p.department}` : ''}</Typography>}
                </TableCell>
                <TableCell><Chip label={p.type} size="small" color={typeColor[p.type]||'default'} /></TableCell>
                <TableCell>{p.duration_years} yr{p.duration_years !== 1 ? 's' : ''}</TableCell>
                <TableCell>{p.total_credits}</TableCell>
                <TableCell><Chip label={p.enrolled_students || 0} size="small" color="success" /></TableCell>
                <TableCell><Chip label={p.total_courses || 0} size="small" color="info" /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>
          {editing ? 'Edit Programme' : 'Add Programme'}
        </DialogTitle>
        <DialogContent sx={{ pt:'20px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Code *" value={form.code} onChange={setF('code')} placeholder="e.g. BSC-CS" />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth size="small" label="Programme Name *" value={form.name} onChange={setF('name')} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="Type" value={form.type} onChange={setF('type')}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Duration (years)" type="number" value={form.duration_years} onChange={setF('duration_years')} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Total Credits" type="number" value={form.total_credits} onChange={setF('total_credits')} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Faculty" value={form.faculty} onChange={setF('faculty')} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Department" value={form.department} onChange={setF('department')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
